'use client';

import type { KeyboardEvent } from 'react';
import type { BundleCardView } from '../../../hooks/useBundleGroups';
import type { BundleVariantsType } from '@/modals/modules/balances/+ShopModal/types/bundles';
import type { DepositStep } from '@/modals/modules/balances/+ShopModal/types/DepositStep';
import type { BundleOfferViewModel } from '@/queries/bundles/bundleOffers.mapper';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Icon } from '@/components/entities/Sprite';
import { Button } from '@/components/ui/Button/button';
import { summarizeSCBalances } from '@/libs/summarizeSCBalances';
import EXTRA_COINS_BADGE from '@/public/assets/icons/shop/extra-coins-badge.svg';
import { normalizeBadges } from '@/queries/bundles/bundleOffers.schema';
import { cn } from '@/utils/cn';
import { formatCentsToCurrencyNumber, formatCompactNumber, formatFiatAmount } from '@/utils/currency.utils';
import { BundleBadges } from './components/BundleBadges';
import { BADGE_BG_CSS_VAR } from './consts';
import { getStylesBundleByVariant } from './utils/utils';
import './bundleCard.css';

type RegularSizeBundleImgType = Partial<Record<BundleVariantsType, { width: number; height: number }>>;

interface BundleCardProps extends Omit<BundleCardView, 'offer'> {
  bundle: BundleOfferViewModel;
  setDepositStep: (step: DepositStep) => void;
  handleBundleSelect: (bundle: BundleOfferViewModel) => void;
}

const notRegularSizesBundleImage: RegularSizeBundleImgType = {
  vip: { width: 290, height: 312 },
  promo: { width: 290, height: 264 },
  gamification: { width: 290, height: 264 },
};

export const BundleCard = ({
  bundle,
  variant,
  className,
  setDepositStep,
  handleBundleSelect,
}: BundleCardProps) => {
  const t = useTranslations('bundles');

  const isRegularBundle = variant === 'regular';
  const isVipBundle = variant === 'vip';
  const isPromoBundle = variant === 'promo';
  const isGamificationBundle = variant === 'gamification';

  const beforeSalePrice = bundle.beforeSalePrice;
  const presentationConfig = bundle.presentationOfferConfig;

  const showBeforeSalePrice = !!beforeSalePrice && !isGamificationBundle;

  const summarizedSC = summarizeSCBalances(bundle.sweepCoins, bundle.bonusSweepCoins);
  const sweepCoinsText = formatCompactNumber(formatCentsToCurrencyNumber(summarizedSC), { minUnit: 'million' });
  const goldCoinsText = formatCentsToCurrencyNumber(bundle.goldCoins).toLocaleString('en-US');

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBundleSelect(bundle);
    }
  };

  const { imgClassNames, wrapperClassNames } = getStylesBundleByVariant(variant);
  const coverSrc = bundle.cover?.key || bundle.cover?.path;
  const badges = normalizeBadges(presentationConfig?.badge);
  const promoBadge = badges.find(({ type }) => type === 'promo');
  const badgeStyles = promoBadge?.styles?.cssStyles;

  return (
    <div
      className="relative"
      role="button"
      tabIndex={0}
      data-node-id={bundle.id}
      onClick={() => handleBundleSelect(bundle)}
      onKeyDown={handleCardKeyDown}
    >
      <BundleBadges
        badges={badges}
        variant={variant}
        setDepositStep={setDepositStep}
        dailyBonusUpTo={presentationConfig?.dailyBonusUpTo}
      />
      {
        !!bundle.bonusSweepCoins && !isRegularBundle && (
          <div className="absolute z-1 top-2.5 right-[-0.563rem] w-[7.063rem] h-7">
            <p className="relative z-1 ml-px text-center mt-px leading-5 text-sm font-medium">
              {`${formatCompactNumber(bundle.bonusSweepCoins, { scale: 100 })} SC ${t('extra')}`}
            </p>
            <Image
              className="drop-shadow-[1px_2px_10px_rgba(0,0,0,0.55)] w-[7.063rem] h-7 absolute inset-0"
              src={EXTRA_COINS_BADGE}
              alt="Extra coins badge"
              width={226}
              height={56}
            />
          </div>
        )
      }

      <div
        style={{
          [BADGE_BG_CSS_VAR]: badgeStyles?.backgroundImage || badgeStyles?.background,
        } as React.CSSProperties}
        className={cn(
          'rounded-2xl relative  overflow-hidden shadow-card px-4 cursor-pointer transition-shadow duration-300',
          wrapperClassNames,
          'hover:shadow-card-hover',
          isRegularBundle && promoBadge && 'badge-stroke-border',
          className,
        )}
      >
        <div className="flex justify-between">
          <div
            className={cn(
              'flex',
              isRegularBundle ? 'flex-row justify-between items-center grow' : 'flex-col',
              (!isRegularBundle && isVipBundle) ? 'gap-6 mt-3' : 'gap-3',
            )}
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <Icon name="sweep-coin" className="size-5" />
                <p className="flex text-xl font-medium leading-6">
                  {sweepCoinsText}
                </p>
                <p className="uppercase text-xs font-medium leading-4">
                  {t('free')}
                </p>
              </div>

              <div className="flex items-center gap-1">
                <Icon name="gold-coin" className="size-5" />
                <p className="flex text-xl font-medium leading-6">
                  {goldCoinsText}
                </p>
              </div>
            </div>
            <div className={cn('flex flex-col gap-1', isRegularBundle ? 'items-end' : 'items-start')}>
              {
                showBeforeSalePrice && (
                  <p className="w-22.5 text-center text-xs leading-3 text-c300 line-through tracking-[-0.03rem]">
                    {formatFiatAmount(beforeSalePrice, bundle.currency)}
                  </p>
                )
              }
              <Button
                variant="primary"
                className="h-8 w-22.5 px-0"
              >
                <p className="text-sm font-medium leading-5">
                  {formatFiatAmount(bundle.price, bundle.currency)}
                </p>
              </Button>
            </div>
          </div>
          {
            !isRegularBundle && !!coverSrc && (
              <div className={cn('rounded-br-[1.125rem] absolute right-0 overflow-hidden', imgClassNames)}>
                <Image
                  src={coverSrc}
                  alt={bundle.name}
                  width={notRegularSizesBundleImage[variant]!.width}
                  height={notRegularSizesBundleImage[variant]!.height}
                  className={cn('object-cover w-[9.063rem]', isPromoBundle ? 'h-33' : 'h-39')}
                />
              </div>
            )
          }
        </div>

      </div>
    </div>
  );
};
